import { ClientEntity } from './clients';
import {IPortfolio} from "./portfolios";

export interface IPerformance {
  clientEntities: ClientEntity[];
  portfolios?: IPortfolio[];
  returnType: string;
  date: Date;
  fromDate?: Date;
  returnPeriod: string;
  returnSpanMonths: number;
  returnSetting: ReturnSetting;
}

export interface AssetPerformanceRequest {
  fromDate: Date;
  toDate: Date;
  clientEntities: ClientEntity[];
  portfolios?: IPortfolio[];
  returnSpanMonths: number;
  returnSetting: ReturnSetting;
}

export interface IPerformanceData {
  snapshots?: IPerformanceSnapshotObject[];
  balances?: IPerformanceBalancesObject[];
  returns?: IPerformanceReturnObject[];
  breakdown?: any[];
  overallReturns?: number;
  openingValue?: number;
  closingValue?: number;
  currency?: string;
  currencySymbol?: string;
  dateRange?: string[];
  overrideInception?: boolean; // flag to determine if the portfolio has historical performance data like Rival
  glInception?: Date;
  historicInception?: Date;
}

export interface IAssetReturn {
  CurrencyImpact: number;
  DeAnnualisedCurrencyImpact: number;
  DeAnnualisedTotalReturn: number;
  DeAnnualisedTotalReturnInSecurityCurrency: number;
  TotalReturn: number;
  TotalReturnInSecurityCurrency: number;
  IsSecuritySupported: boolean;
  AssetHeldForLessThanAYear: boolean;
  NotSupportedReason: NotSupportedReason;
}

export interface IAssetPerformanceData {
  SecurityId: number;
  Name: string;
  ManagerAssetClassification: string;
  FromDate: Date;
  ToDate: Date;
  OpeningMarketValue: IMoneyObject;
  ClosingMarketValue: IMoneyObject;
  PurchaseSales: IMoneyObject;
  CapitalTransferred: IMoneyObject;
  RealisedGains: IMoneyObject;
  UnRealisedGains: IMoneyObject;
  TransferredUnrealisedGains: IMoneyObject
  AccruedInterest: IMoneyObject;
  GrossIncome: any;
  Expenses: any;
  AssetReturn: IAssetReturn;
  DeAnnualisedXirr: number;
  XirrInSecurityCurrency: number;
  DeAnnualisedXirrInSecurityCurrency: number;
  CurrencyGain: number;
  PortfolioWeight: number;
  OpeningWeight: number;
  IsSecuritySupported: boolean;
  SubType: number;
  AdditionalMetadata: { Key: string, Value: string }[];
  ContributionToReturn: number;
}

export enum NotSupportedReason
{
  SecurityConversionWithNoTransferredUnrealisedGains = 0,
  SecurityUnsupported = 1,
  InvalidMovements = 2,
  Cash = 3,
  ReturnValueTooLarge = 4,
}

export interface IMoneyObject {
  Currency: number;
  Value: number;
  Desc: string;
}

export interface IKiwiSaverDashboardSummaryDataProperty {
  value: number;
  caption: string;
}

export interface IKiwiSaverDashboardSummaryData {
  currency: string;
  closingValue: IKiwiSaverDashboardSummaryDataProperty;
  balance: IKiwiSaverDashboardSummaryDataProperty;
  yourContributions: IKiwiSaverDashboardSummaryDataProperty;
  employersContributions: IKiwiSaverDashboardSummaryDataProperty;
  governmentContributions: IKiwiSaverDashboardSummaryDataProperty;
  otherContributions: IKiwiSaverDashboardSummaryDataProperty;
  returns: IKiwiSaverDashboardSummaryDataProperty;
  withdrawals: IKiwiSaverDashboardSummaryDataProperty;
  fees: IKiwiSaverDashboardSummaryDataProperty;
  tax: IKiwiSaverDashboardSummaryDataProperty;
  loading: boolean;
  retrieveData: boolean;
  settings: {
    selectedReturnSpanMonths: number;
  };
}

export interface PerformanceSettings {
  selectedReturnSpanMonths: number;
  selectedReturnPeriod: string;
  selectedReturnType: Return;
  selectedReturnSymbol: ReturnSymbol;
}

export interface IPerformanceDataResult {
  performanceData: IPerformanceData;
  date: Date;
  clientEntities: ClientEntity[];
  portfolios?: IPortfolio[];
}

export interface IAssetPerformanceDataResult {
  assetPerformanceData: IAssetPerformanceData[];
  date: Date;
  clientEntities: ClientEntity[];
  portfolios: IPortfolio[];
}

export enum ReturnTypes {
  XIRR = '0', //(Previously called 'Portfolio')
  TWR = '1', //(Previously called 'Advisor')
}

export type Return = ReturnTypes.XIRR | ReturnTypes.TWR;

export enum ReturnSpanMonths {
  CUSTOM = -9,
  HISTORIC_INCEPTION = -1,
  INCEPTION = 0,
  MONTH = 1,
  QUARTER = 3,
  YEAR = 12,
  THREE_YEARS = 36,
  FIVE_YEARS = 60,
}

export enum DateSelectorCaption {
  HISTORIC_INCEPTION = 'Historic Inception',
  INCEPTION = 'Inception',
  MONTH = 'Month',
  QUARTER = 'Quarter',
  YEAR = '12 Months',
  THREE_YEARS = '3 Years',
  FIVE_YEARS = '5 Years',
}

export enum InceptionCaption {
  INCEPTION = 'Since Inception',
  SINCE_JOINING_ADMINIS = 'Since joining Adminis',
  HISTORIC_INCEPTION = 'Since Historic Inception',
}

export enum ReturnSetting {
  AFTER_TAX = 0,
  BEFORE_TAX = 1,
}

export enum ReturnSymbol {
  PERCENT = '%',
  DOLLARS = '$',
}

export enum ReturnPeriod {
  MONTH = 'Month',
  QUARTER = 'Quarter',
}

export enum ReturnSpanPeriodCaptions {
  MONTH = 'View by month',
  QUARTER = 'View by quarter'
}

export interface IPerformanceSnapshotObject {
  caption: string;
  date: string;
  return: number;
  returnInCurrency: number;
}

export interface IPerformancePageSnapshot extends IPerformanceSnapshotObject {
  months: ReturnSpanMonths;
  overrideCaption: string;
  showInReturns: boolean;
  showFalseInceptionTooltip: boolean;
}

export interface IPerformanceBalancesObject {
  caption: string;
  accountSubType: AccountSubType;
  value: number;
}

export interface IPerformanceReturnObject {
  date: string;
  return: number[];
  returnInCurrency: IReturnInCurrency;
}

export interface IReturnInCurrency {
  Currency: number;
  CurrencyCode: string;
  IsNull: boolean;
  Value: number;
}


export enum AccountSubType {
  
  Securities = 1100,
  
  // KiwiSaver - contributions
  KiwiSaverGovernmentContribution = 3041,
  KiwiSaverEmployerContribution = 3042,
  KiwiSaverEmployeeContribution = 3043,
  KiwiSaverVoluntaryContribution = 3044,
  KiwiSaverAUSSuperContribution = 3045,
  KiwiSaverOtherSuperContribution = 3046,
  // Tax
  TaxPaid = 7010,
  TaxWithheld = 7020,
  // Withdrawals
  CapitalRedeemed = 3001,
  CapitalDistributed = 3015,
  CashSweep = 3030,
  KiwiSaverAdminError = 3050,
  KiwiSaverCourtOrder = 3051,
  KiwiSaverDD_Dishonour = 3052,
  KiwiSaverDeath = 3053,
  KiwiSaverFinancialHardship = 3054,
  KiwiSaverFirstHomePurchase = 3055,
  KiwiSaverInvalidEnrolment = 3056,
  KiwiSaverIRDRefund = 3057,
  KiwiSaverLateOptOut = 3058,
  KiwiSaverMortgageDiversion = 3059,
  KiwiSaverEmigrationAUS = 3060,
  KiwiSaverEmigrationElsewhere = 3061,
  KiwiSaverRetirement = 3062,
  KiwiSaverSeriousIllness = 3063,
  KiwiSaverTransferOut = 3064,
}

export interface IHolding {
  name: string;
  allocation: string;
  market_value: number;
  weighting: number;
  performance: number;
  detail: IHoldingDetail[];
  subType: number;
  additionalMetadata: { Key: string, Value: string }[];
  is_security_supported: boolean;
  not_supported_reason: number;
  contribution_to_return: number;
}


export interface IHoldingDetail {
  opening_weight: number;
  opening_value: number;
  purchases_sales: number;
  capital_transferred: number;
  realised_gain: number;
  unrealised_gain: number;
  transferred_unrealised_gain: number;
  accrued_interest: number,
  gross_income: number;
  expenses: number;
  asset_return: number;
  currency_impact: number;
  total_return: number;
}

export const MODEL_VIEW_UNIT_TYPES = [
  {
    symbol: ReturnSymbol.PERCENT,
    label: 'Percent',
  },
  {
    symbol: ReturnSymbol.DOLLARS,
    label: 'Currency',
  },
];

export const MODEL_VIEW_RETURN_SETTING = [
  {
    symbol: ReturnSetting.AFTER_TAX,
    label: 'After tax',
    description: 'Performance \'after tax\' includes the impact of tax.',
  },
  {
    symbol: ReturnSetting.BEFORE_TAX,
    label: 'Before tax',
    description: 'Performance \'before tax\' excludes tax impacts.',
  },
];

export interface IUnitTypes {
  name: string;
  symbol: string;
  label: string;
}

export const MODEL_RETURN_TYPES = [
  {
    type: ReturnTypes.XIRR,
    label: 'My returns',
    description:
      'My Returns (XIRR) incorporates the size and timing of cashflows.',
  },
  {
    type: ReturnTypes.TWR,
    label: 'Investment returns',
    description:
      'Investment Returns (TWR) excludes your deposits and withdraws, showing the effect of the market and manager’s choices over the investments in your portfolio.',
  },
];

export interface IReturnTypes {
  name: string;
  label: string;
  description: string;
}

export const MODEL_PERIODS = [
  {
    months: 1,
    label: '1 month',
  },
  {
    months: 3,
    label: '3 months',
  },
  {
    months: 12,
    label: '1 year',
  },
  {
    months: 36,
    label: '3 years',
  },
  {
    months: 60,
    label: '5 years',
  },
  {
    months: 0,
    label: InceptionCaption.INCEPTION,
  },
  {
    months: -1,
    label: InceptionCaption.HISTORIC_INCEPTION,
  },
  {
    months: -9,
    label: 'Custom date range',
  },
];

export interface IPeriods {
  months: number;
  label: string;
}

export const MODEL_HOLDINGS_COLUMNS = [
  {
    name: 'name',
    label: 'Name',
  },
  {
    name: 'allocation',
    label: 'Asset allocation',
  },
  {
    name: 'market_value',
    label: 'Closing value',
    class: 'amount',
  },
  {
    name: 'weighting',
    label: 'Closing portfolio weight',
    class: 'amount',
  },
  {
    name: 'performance',
    label: 'Performance',
    class: 'amount',
  },
  {
    name: 'contribution_to_return',
    label: 'Contribution to return',
    tooltipText: 'This showcases the assets as a percentage that contributes to your portfolio return e.g. your portfolio return is 5%, this will show how each asset contributes to the 5%. Assets are weighted by their opening balances for the period.',
    class: 'amount',
  },
  {
    name: 'opening_weight',
    label: 'Opening portfolio weight',
    class: 'amount',
  },
];

export interface IPerformanceFilterItem {
  SelectedStartDate: Date;
  SelectedEndDate: Date;
  LabelChosenPeriod: string;
  ChosenPeriod: number;
}

export interface IPerformanceDataInception {
  glInception ?: Date;
  historicInception ?: Date;
  overrideInception : boolean;
}

export interface PerformanceFilter {
  entities: ClientEntity[],
  portfolios: IPortfolio[],
  period: ReturnSpanMonths,
  endDate: string,
  startDate: string,
  returnType:ReturnTypes,
  returnSymbol: ReturnSymbol,
  returnSetting: ReturnSetting,
}
